import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
	ICartOrderInfo,
	IOfferContext,
	ITypeVignettesOrderCompletedFields,
	IVignetteContext,
	Locales,
	OfferContext,
	OrderService,
	VignetteContext,
	countryUrlMap,
	formatTripsAmountLabel,
	getLabelValue,
	getPriceAmountByCatalogId,
	getServiceFeePerProduct,
	getTotalServiceFee,
} from '@adac-mp/shared';
import { useParams } from 'react-router-dom';
import { IconPopup, Typography, getDrawerTitle, getOneCartCountries } from '../../index';

interface IOrderSummaryAccordionProps {
	orderInfo: ICartOrderInfo;
	hasMembership: boolean;
}

type ProductStatus = 'confirmed' | 'in process' | 'cancelled' | undefined;

export const OrderSummaryAccordion = ({ orderInfo: initialOrderInfo, hasMembership }: IOrderSummaryAccordionProps) => {
	const { labels } = useContext<IOfferContext>(OfferContext);
	const { lang } = useParams();
	const { contentfulLabels } = useContext<IVignetteContext>(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignettesOrderCompletedFields;
	const countries = getOneCartCountries(currentLabels?.oneCartCountries?.items);
	const [orderInfo, setOrderInfo] = useState<ICartOrderInfo>(initialOrderInfo);
	const fetchBookingStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);

	const fetchBookingInfo = async () => {
		const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken');

		await OrderService.getBookingInfo(
			{
				orderHash: orderInfo.orderHash,
				orderId: orderInfo.orderId,
				token,
			},
			lang as Locales,
		).then((res) => {
			if (res?.code === 200) {
				setOrderInfo(res.orderInfo);
				sessionStorage.setItem('orderInfo', JSON.stringify(res.orderInfo));
			}
		});
	};

	const clearFetchBookingStatusInterval = () => {
		if (fetchBookingStatusIntervalRef.current) {
			clearInterval(fetchBookingStatusIntervalRef.current);
			fetchBookingStatusIntervalRef.current = null;
		}
	};

	const startFetchBookingStatusInterval = () => {
		if (fetchBookingStatusIntervalRef.current) return;

		let attemptCount = 0;
		const maxAttempts = 15;

		fetchBookingInfo();

		fetchBookingStatusIntervalRef.current = setInterval(() => {
			if (attemptCount >= maxAttempts) {
				clearFetchBookingStatusInterval();
				return;
			}

			fetchBookingInfo();
			attemptCount++;
		}, 30000); // 30 seconds
	};

	useEffect(() => {
		const hasSchweizProduct = orderInfo.supplierGroupItems.some(({ supplierId }) => supplierId === 14);

		if (!hasSchweizProduct) return;

		startFetchBookingStatusInterval();

		return () => clearFetchBookingStatusInterval();
	}, []);

	const renderAccordionRow = (productContent: ReactNode, price: string, status?: ReactNode, key?) => (
		<div key={key} className={`grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,3fr)_1fr_1fr] mb-3 last:mb-0`}>
			<Typography component="p" color="title" size="baseLg" className="text-left">
				{productContent}
			</Typography>
			<Typography component="p" color="title" size="baseLg" className="text-right max-w-1/2">
				{price}
			</Typography>
			{status}
		</div>
	);

	const renderProductStatus = (isSchweiz: boolean, currentStatus?: ProductStatus) => {
		let statusColor = '#2E7D32';
		let statusText = currentLabels?.productStatusDone || 'abgeschlossen';

		if (isSchweiz) {
			switch (currentStatus) {
				case 'confirmed':
					statusColor = '#2E7D32';
					statusText = currentLabels?.productStatusDone || 'abgeschlossen';
					clearFetchBookingStatusInterval();
					break;
				case 'cancelled':
					statusColor = '#C33';
					statusText = currentLabels?.productStatusCancelled || 'fehlgeschlagen und storniert';
					clearFetchBookingStatusInterval();
					break;
				case 'in process':
				default:
					statusColor = '#FC0';
					statusText = currentLabels?.productStatusPending || 'in Bearbeitung';
					break;
			}
		}

		return (
			<div className="flex items-center gap-1.5 mt-1.5 md:mt-0">
				<div
					style={{
						backgroundColor: statusColor,
						width: '10px',
						height: '10px',
						borderRadius: '2px',
						display: 'flex',
						flexShrink: 0,
					}}
				/>
				<div className="text-left">{statusText}</div>
			</div>
		);
	};

	return (
		<>
			<Typography component="p" variant={'title'} size="xlEven" className={'w-full mb-5'} data-cy="order-number">
				{currentLabels?.orderNumber || 'Bestellnummer'}: {orderInfo.orderNo}
			</Typography>

			<Typography
				component="h4"
				variant={'title'}
				size="xlEven"
				className={'w-full md:mb-4 text-left md:text-center flex md:block gap-x-1 items-center'}
				data-cy="order-number">
				{currentLabels?.orderOverview || 'Bestellübersicht'}

				<div className="md:hidden flex">
					<IconPopup
						bold
						contentfulId={'productStatus'}
						iconSize={'24'}
						title={getDrawerTitle('productStatus', currentLabels?.helpDrawers)}
						type="info"
						paddingX="large"
					/>
				</div>
			</Typography>

			<div
				className="bg-white md:border-solid border-gray-500 md:border md:px-4 md:py-3.5 rounded-lg w-full mb-5"
				data-cy="order-summary">
				<div className="hidden md:grid grid-cols-[minmax(0,3fr)_1fr_1fr] text-left">
					<Typography component="h4" variant={'title'} size="xlEven" className={''} data-cy="order-number">
						{getLabelValue('mp.article', labels) || 'Produkt'}
					</Typography>
					<Typography component="h4" variant={'title'} size="xlEven" className={''} data-cy="order-number">
						{getLabelValue('mp.price', labels) || 'Preis'}
					</Typography>
					<div className="flex items-center gap-1.5">
						<Typography
							component="h4"
							variant={'title'}
							size="xlEven"
							className={''}
							data-cy="order-number">
							Status
						</Typography>
						<IconPopup
							bold
							contentfulId={'productStatus'}
							iconSize={'24'}
							title={getDrawerTitle('productStatus', currentLabels?.helpDrawers)}
							type="info"
							paddingX="large"
						/>
					</div>
				</div>

				<div className="py-3 border-b border-t-0 border-l-0 border-r-0 border-solid border-gray-500">
					{orderInfo.supplierGroupItems.map(
						({ supplierId, productDetails, sumServiceFee, sumDiscount }, id) => {
							const serviceFee = getTotalServiceFee(sumDiscount, sumServiceFee, hasMembership);
							const serviceFeePerProduct = getServiceFeePerProduct(
								sumDiscount,
								sumServiceFee,
								productDetails.length,
								hasMembership,
							);

							return (
								<div
									key={`group-${id}`}
									className="py-3 border-solid border-t border-b-0 border-l-0 border-r-0 border-gray-500 last:pb-0">
									<div className={`flex justify-between gap-1.5 md:mb-3 last:mb-0`}>
										<Typography variant="title" color="title" size="baseLg">
											{
												countries?.find(
													(country) => country.countryId === countryUrlMap[supplierId],
												)?.country
											}
										</Typography>
									</div>
									{productDetails.map((product, index) => {
										const productCatalogName = (
											<div className="flex flex-col">
												<div>{product.serviceCatalogName}</div>
												{product.sectionName && product.moduleTypeId === 3 && (
													<div>
														{product.sectionName}{' '}
														{`(${formatTripsAmountLabel(product.trips, labels)})`}
													</div>
												)}
											</div>
										);

										const productStatus = renderProductStatus(
											product.supplierId === 14,
											product.bazgStatus as ProductStatus,
										);

										return renderAccordionRow(
											productCatalogName,
											product.flexProduct
												? currentLabels?.priceDetails?.flexServiceLabel ||
														'FLEX Modell zzgl. Mautgebühren'
												: `${getPriceAmountByCatalogId(product.priceComponents)}€`,
											productStatus,
											index,
										);
									})}
									{sumServiceFee.decimalAmount > 0 &&
										renderAccordionRow(
											`${currentLabels?.priceDetails?.serviceFee || 'Servicegebühr'} (
													${serviceFeePerProduct} / ${getLabelValue('mp.article', labels) || 'Artikel'})`,
											serviceFee,
										)}
									{sumDiscount.decimalAmount > 0 &&
										hasMembership &&
										renderAccordionRow(
											currentLabels?.priceDetails?.serviceDiscount || 'Servicerabatt',
											`-${sumDiscount.amount}€`,
										)}
								</div>
							);
						},
					)}
					{hasMembership}
				</div>
				<div
					className={`flex justify-between
					gap-1.5 mt-3 mb-3 last:mb-0`}>
					<div className="text-left flex-1">
						<Typography component="p" variant="title" color="title" size="xxlEven">
							{currentLabels?.priceDetails?.totalPriceHeadline || 'Gesamtpreis'}
							<br />
							<Typography color="text" className="font-normal mt-1.5">
								{getLabelValue('mp.vat', labels) || 'inkl. MwSt.'}
							</Typography>
						</Typography>
					</div>
					<Typography
						data-cy="order-price"
						component="p"
						variant="title"
						color="title"
						size="xxlEven"
						className="flex-1 text-right">
						{orderInfo?.price ? `${orderInfo.price.amount}€` : ''}
					</Typography>
				</div>
			</div>
		</>
	);
};
